import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Stack,
  TextField,
  MenuItem,
  Card,
  CardContent
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent';
import MemoryIcon from '@mui/icons-material/Memory';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import TimelineIcon from '@mui/icons-material/Timeline';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import { useApi } from '../contexts/ApiContext';

const isNonDialApplication = (appName = '') => {
  const normalized = String(appName || '').trim().toLowerCase();
  if (!normalized) return false;
  if (normalized === 'stasis(cti-ari)') return true;
  if (normalized === '(none)') return true;
  if (normalized.includes('chanspy')) return true;
  if (normalized.includes('spy')) return true;
  if (normalized.includes('monitor')) return true;
  if (normalized.includes('confbridge')) return true;
  return false;
};

const getExcludedChannelsFromBreakdown = (perApplication = {}) =>
  Object.entries(perApplication || {}).reduce((acc, [appName, value]) => {
    if (!isNonDialApplication(appName)) return acc;
    return acc + (Number(value) || 0);
  }, 0);

const formatOverdialingLabel = (value = '') => {
  if (!value) return '';
  const normalized = value.toLowerCase();
  if (normalized.includes('predict')) {
    return `Modo predictivo: ${value}`;
  }
  if (normalized.includes('progres')) {
    return `Modo progresivo: ${value}`;
  }
  return value;
};

const PERSISTENT_CAMPAIGN_FILTER_KEY = 'engine_status_campaign_filter';
const PERSISTENT_MODE_FILTER_KEY = 'engine_status_mode_filter';

const isHiddenDialingMode = (mode = '') => {
  const normalized = String(mode || '').trim().toLowerCase();
  return normalized === 'manual' || normalized === 'preview';
};

const EngineOscillogram = ({ dialerHistory = [], asteriskHistory = [] }) => {
  const WIDTH = 220;
  const HEIGHT = 100;
  const PADDING_TOP = 10;
  const PADDING_BOTTOM = 14;
  const INNER_HEIGHT = HEIGHT - PADDING_TOP - PADDING_BOTTOM;
  const maxPoints = 30;
  const dialerData = Array.isArray(dialerHistory) ? dialerHistory.slice(-maxPoints) : [];
  const asteriskData = Array.isArray(asteriskHistory) ? asteriskHistory.slice(-maxPoints) : [];

  const { dialerPath, asteriskPath } = useMemo(() => {
    const maxValue = Math.max(1, ...dialerData, ...asteriskData);
    const buildPath = (values) => {
      if (!values.length) {
        return `M0,${HEIGHT / 2} L${WIDTH},${HEIGHT / 2}`;
      }
      const normalized = values.length === 1 ? [values[0], values[0]] : values;
      const denominator = Math.max(normalized.length - 1, 1);
      return normalized
        .map((value, index) => {
          const x = (index / denominator) * WIDTH;
          const y = PADDING_TOP + (INNER_HEIGHT - (value / maxValue) * INNER_HEIGHT);
          return `${index === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
        })
        .join(' ');
    };

    return {
      dialerPath: buildPath(dialerData),
      asteriskPath: buildPath(asteriskData)
    };
  }, [dialerData, asteriskData]);

  return (
    <Box sx={{ width: '100%' }}>
      <svg width="100%" height="100" viewBox={`0 0 ${WIDTH} ${HEIGHT}`} preserveAspectRatio="none">
        <rect x="0" y="0" width={WIDTH} height={HEIGHT} fill="#050d1a" rx={6} />
        <path
          d={`M0,${(PADDING_TOP + INNER_HEIGHT / 2).toFixed(1)} L${WIDTH},${(PADDING_TOP + INNER_HEIGHT / 2).toFixed(1)}`}
          stroke="#112240"
          strokeWidth="1"
          strokeDasharray="4 6"
        />
        <path d={asteriskPath} stroke="#42a5f5" strokeWidth="2" fill="none" />
        <path d={dialerPath} stroke="#00e676" strokeWidth="2" fill="none" />
      </svg>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap' }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#00e676' }} />
          <Typography variant="caption" color="text.secondary">Dialer</Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#42a5f5' }} />
          <Typography variant="caption" color="text.secondary">PBX</Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

const Sparkline = ({ values = [], color = '#667eea' }) => {
  const cleanValues = Array.isArray(values) ? values.map((v) => Number(v) || 0) : [];
  const data = cleanValues.slice(-30);
  if (data.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        Sin datos de rendimiento
      </Typography>
    );
  }
  const normalized = data.length === 1 ? [data[0], data[0]] : data;
  const maxValue = Math.max(...normalized, 1);
  const denominator = Math.max(normalized.length - 1, 1);
  const points = normalized
    .map((value, index) => {
      const x = (index / denominator) * 100;
      const y = 40 - (value / maxValue) * 40;
      return `${index === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  return (
    <Box sx={{ mt: 1 }}>
      <svg width="100%" height="50" viewBox="0 0 100 40" preserveAspectRatio="none">
        <polyline
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          points={points}
        />
      </svg>
      <Typography variant="caption" color="text.secondary">
        Historial reciente (últimos {normalized.length} lecturas)
      </Typography>
    </Box>
  );
};

const StatCard = ({ title, value, helper, icon, footer }) => (
  <Box
    sx={{
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      p: 2,
      height: '100%',
      bgcolor: 'background.paper'
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box
        sx={{
          p: 1,
          borderRadius: '50%',
          bgcolor: 'primary.light',
          color: 'primary.main',
          display: 'inline-flex'
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="overline" color="text.secondary">
          {title}
        </Typography>
        <Typography variant="h5" sx={{ wordBreak: 'break-word' }}>
          {value}
        </Typography>
      </Box>
    </Stack>
    {helper && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {helper}
      </Typography>
    )}
    {footer}
  </Box>
);

const EngineStatusPage = () => {
  const { dialer, skills: skillsApi } = useApi();
  const [engineMap, setEngineMap] = useState({});
  const [skills, setSkills] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [campaignFilter, setCampaignFilter] = useState(() => {
    if (typeof window === 'undefined') return '';
    return window.localStorage.getItem(PERSISTENT_CAMPAIGN_FILTER_KEY) || '';
  });
  const [statusFilter, setStatusFilter] = useState('active');
  const [modeFilter, setModeFilter] = useState(() => {
    if (typeof window === 'undefined') return '';
    return window.localStorage.getItem(PERSISTENT_MODE_FILTER_KEY) || '';
  });
  const [detailHistory, setDetailHistory] = useState([]);
  const [engineHistory, setEngineHistory] = useState({});
  const [asteriskSummary, setAsteriskSummary] = useState({
    activeCalls: 0,
    activeChannels: 0,
    realDialChannels: 0,
    excludedChannels: 0
  });
  const [asteriskHistory, setAsteriskHistory] = useState({});
  const [asteriskPerSkill, setAsteriskPerSkill] = useState({});
  const [asteriskBreakdown, setAsteriskBreakdown] = useState({ perState: {}, perApplication: {} });
  const [totalDialerHistory, setTotalDialerHistory] = useState([]);
  const [totalAsteriskHistory, setTotalAsteriskHistory] = useState([]);
  const pollingRef = useRef(false);

  const detailOverdialingLabel = useMemo(() => {
    if (!detail && !selectedSkill) return '';
    const value = detail?.overdialing_mode || selectedSkill?.overdialing_mode || '';
    return formatOverdialingLabel(value);
  }, [detail, selectedSkill]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(PERSISTENT_CAMPAIGN_FILTER_KEY, campaignFilter || '');
  }, [campaignFilter]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(PERSISTENT_MODE_FILTER_KEY, modeFilter || '');
  }, [modeFilter]);

  const visibleSkills = useMemo(
    () => skills.filter((skill) => !isHiddenDialingMode(skill.dialing_mode)),
    [skills]
  );

  const campaignOptions = useMemo(() => {
    const set = new Map();
    visibleSkills.forEach(skill => {
      const value = skill.idcampaign ?? skill.campaign ?? skill.campaign_name ?? '';
      if (value === '' || value === null || value === undefined) return;
      const label = skill.campaign_name || `Campaña ${value}`;
      if (!set.has(value)) {
        set.set(value, label);
      }
    });
    return Array.from(set.entries()).map(([value, label]) => ({ value, label }));
  }, [visibleSkills]);

  const modeOptions = useMemo(() => {
    const set = new Set();
    visibleSkills.forEach(skill => {
      if (skill.dialing_mode && !isHiddenDialingMode(skill.dialing_mode)) {
        set.add(skill.dialing_mode);
      }
    });
    return Array.from(set).map(value => ({ value, label: value }));
  }, [visibleSkills]);

  useEffect(() => {
    if (!modeFilter) return;
    if (!modeOptions.length) return;
    const exists = modeOptions.some((option) => option.value.toLowerCase() === modeFilter.toLowerCase());
    if (!exists) {
      setModeFilter('');
    }
  }, [modeFilter, modeOptions]);

  const filteredSkills = useMemo(() => {
    return visibleSkills.filter(skill => {
      const campaignValue = skill.idcampaign ?? skill.campaign ?? skill.campaign_name ?? '';
      const normalizeStatus = Number(skill.status ?? (engineMap[skill.skilldef]?.isRunning ? 1 : 0));
      if (campaignFilter && String(campaignValue) !== String(campaignFilter)) return false;
      if (statusFilter === 'active' && normalizeStatus !== 1) return false;
      if (statusFilter === 'inactive' && normalizeStatus === 1) return false;
      if (modeFilter && (skill.dialing_mode || '').toLowerCase() !== modeFilter.toLowerCase()) return false;
      return true;
    });
  }, [visibleSkills, engineMap, campaignFilter, statusFilter, modeFilter]);

  const cardEngine = (skill) => engineMap[skill.skilldef] || null;
  const isRunning = (skill) => {
    const engine = cardEngine(skill);
    if (engine) return !!engine.isRunning;
    return Number(skill.status) === 1;
  };

  const getCampaignLabel = (skill) => {
    const value = skill.idcampaign ?? skill.campaign;
    if (!value) return skill.campaign_name || 'Sin campaña';
    const match = campaignOptions.find(option => String(option.value) === String(value));
    return match ? match.label : (skill.campaign_name || `Campaña ${value}`);
  };

const getModeLabel = (skill) => skill.dialing_mode || cardEngine(skill)?.mode || 'N/D';

  const loadEngines = React.useCallback(async (options = {}) => {
    if (!dialer?.getEngines) return;
    const { silent = false } = options;
    if (!silent) {
      setListLoading(true);
      setListError('');
    }
    try {
      const response = await dialer.getEngines();
      const list = response?.data ?? response ?? [];
      const map = {};
      let totalCalls = 0;
      list.forEach(engine => {
        const def = engine?.skillDef || engine?.skilldef;
        if (def) map[def] = engine;
        const stats = engine.statistics || engine;
        totalCalls += Number(stats?.callsInProgress ?? 0);
      });
      setEngineMap(map);
      setEngineHistory((prev) => {
        const next = { ...prev };
        list.forEach(engine => {
          const def = engine?.skillDef || engine?.skilldef;
          if (!def) return;
          const stats = engine.statistics || engine;
          const value = Number(stats?.callsInProgress ?? 0);
          const history = Array.isArray(next[def]) ? [...next[def]] : [];
          history.push(value);
          if (history.length > 60) history.shift();
          next[def] = history;
        });
        return next;
      });
      setTotalDialerHistory((prev) => {
        const next = [...prev, totalCalls];
        if (next.length > 60) next.shift();
        return next;
      });
    } catch (error) {
      if (!silent) {
        setListError(error?.message || 'No se pudo obtener el estado de los motores');
        setEngineMap({});
      }
    } finally {
      if (!silent) {
        setListLoading(false);
      }
    }
  }, [dialer]);

  const loadAsteriskChannels = React.useCallback(async () => {
    if (!dialer?.getAsteriskChannels) return;
    try {
      const response = await dialer.getAsteriskChannels();
      const data = response?.data ?? response ?? {};
      const activeCalls = Number(data.activeCalls ?? 0);
      const activeChannels = Number(data.activeChannels ?? 0);
      const perSkillChannels = data.perSkillChannels && typeof data.perSkillChannels === 'object'
        ? data.perSkillChannels
        : {};
      const breakdown = data.breakdown && typeof data.breakdown === 'object'
        ? data.breakdown
        : { perState: {}, perApplication: {} };
      const perApplication = breakdown.perApplication || {};
      const excludedChannels = getExcludedChannelsFromBreakdown(perApplication);
      const realDialChannels = Math.max(0, activeChannels - excludedChannels);
      setAsteriskSummary({ activeCalls, activeChannels, realDialChannels, excludedChannels });
      setAsteriskPerSkill(perSkillChannels);
      setAsteriskBreakdown({
        perState: breakdown.perState || {},
        perApplication
      });
      setAsteriskHistory((prev) => {
        const next = { ...prev };
        Object.entries(perSkillChannels).forEach(([skillDef, value]) => {
          const history = Array.isArray(next[skillDef]) ? [...next[skillDef]] : [];
          history.push(Number(value) || 0);
          if (history.length > 60) history.shift();
          next[skillDef] = history;
        });
        return next;
      });
      setTotalAsteriskHistory((prev) => {
        const next = [...prev, realDialChannels];
        if (next.length > 60) next.shift();
        return next;
      });
    } catch (error) {
      // Ignore polling errors to avoid blocking UI
    }
  }, [dialer]);

  const truncateLabel = (label = '', max = 50) => {
    if (!label) return '';
    if (label.length <= max) return label;
    return `${label.slice(0, Math.max(max - 1, 0))}…`;
  };

  const renderBreakdownList = (items = {}, emptyLabel) => {
    const entries = Object.entries(items || {});
    if (!entries.length) {
      return (
        <Box
          sx={{
            minHeight: 170,
            maxHeight: 170,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {emptyLabel}
          </Typography>
        </Box>
      );
    }
    return (
      <Box
        sx={{
          minHeight: 170,
          maxHeight: 170,
          overflowY: 'auto',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 1
        }}
      >
        <Stack spacing={0.25}>
          {entries
            .sort((a, b) => b[1] - a[1])
            .map(([label, value]) => (
              <Stack direction="row" spacing={0.75} key={label} alignItems="center" sx={{ minHeight: 20 }}>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {truncateLabel(label)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {Number(value).toLocaleString()}
                </Typography>
              </Stack>
            ))}
        </Stack>
      </Box>
    );
  };

  const renderBreakdownTwoCols = (items = {}, emptyLabel) => {
    const entries = Object.entries(items || {}).sort((a, b) => b[1] - a[1]);
    if (!entries.length) {
      return (
        <Typography variant="caption" color="text.secondary">
          {emptyLabel}
        </Typography>
      );
    }
    const midpoint = Math.ceil(entries.length / 2);
    const left = entries.slice(0, midpoint);
    const right = entries.slice(midpoint);
    const renderCol = (colEntries) => (
      <Stack spacing={0.5} sx={{ minWidth: 0 }}>
        {colEntries.map(([label, value]) => (
          <Stack direction="row" spacing={1} key={label} justifyContent="space-between">
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0 }}>
              {truncateLabel(label)}
            </Typography>
            <Typography variant="caption">{Number(value).toLocaleString()}</Typography>
          </Stack>
        ))}
      </Stack>
    );
    return (
      <Grid container spacing={1}>
        <Grid item xs={12} md={6}>
          {renderCol(left)}
        </Grid>
        <Grid item xs={12} md={6}>
          {renderCol(right)}
        </Grid>
      </Grid>
    );
  };

  const loadSkills = React.useCallback(async () => {
    if (!skillsApi?.getAll) return;
    try {
      const response = await skillsApi.getAll();
      const data =
        response?.data?.skills ||
        response?.data ||
        response?.skills ||
        response ||
        [];
      setSkills(Array.isArray(data) ? data : []);
    } catch (error) {
      setSkills([]);
      setListError(prev => prev || error?.message || 'No se pudo cargar la lista de skills');
    }
  }, [skillsApi]);

  const loadDetail = React.useCallback(async (skillDef, options = {}) => {
    if (!dialer?.getEngine || !skillDef) return;
    const { silent = false } = options;
    if (!silent) {
      setDetailLoading(true);
      setDetailError('');
    }
    try {
      const response = await dialer.getEngine(skillDef);
      setDetail(response?.data ?? response ?? null);
    } catch (error) {
      if (!silent) {
        setDetail(null);
        setDetailError(error?.message || 'No se pudo obtener el detalle del motor');
      }
    } finally {
      if (!silent) {
        setDetailLoading(false);
      }
    }
  }, [dialer]);

  useEffect(() => {
    loadSkills();
    loadEngines();
  }, [loadSkills, loadEngines]);

  useEffect(() => {
    let isMounted = true;
    const tick = async () => {
      if (!isMounted || pollingRef.current) return;
      pollingRef.current = true;
      try {
        await Promise.all([
          loadEngines({ silent: true }),
          loadAsteriskChannels(),
          selectedSkill?.skilldef
            ? loadDetail(selectedSkill.skilldef, { silent: true })
            : Promise.resolve()
        ]);
      } finally {
        pollingRef.current = false;
      }
    };

    tick();
    const intervalId = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      tick();
    }, 2000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [loadEngines, loadAsteriskChannels, loadDetail, selectedSkill?.skilldef]);

  useEffect(() => {
    if (selectedSkill?.skilldef) {
      loadDetail(selectedSkill.skilldef);
    } else {
      setDetail(null);
      setDetailError('');
    }
  }, [selectedSkill?.skilldef, loadDetail]);

  useEffect(() => {
    if (!selectedSkill?.skilldef) {
      setDetailHistory([]);
      return;
    }
    if (!detail?.statistics) {
      setDetailHistory([]);
      return;
    }

    setDetailHistory((prev) => {
      const next = [...prev, detail.statistics.callsInProgress ?? 0];
      if (next.length > 60) {
        next.shift();
      }
      return next;
    });
  }, [detail?.statistics, detail?.statistics?.callsInProgress, selectedSkill?.skilldef]);

  const handleSelectSkill = (skill) => {
    setSelectedSkill(skill);
  };

  const detailMetrics = useMemo(() => {
    if (!detail) return null;
    const stats = detail.statistics || {};
    const config = detail.config || {};
    const statusLabel = detail.isRunning ? 'Activo' : 'Detenido';
    const modeLabel = detail.mode || config.mode || 'N/D';
    const factorValue = stats.factor ?? config.factor ?? 0;
    const factorDisplay =
      typeof factorValue === 'number' ? factorValue.toFixed(2) : factorValue;
    const readonlyLastUpdate = new Date(stats.lastUpdate || Date.now()).toLocaleTimeString();

    return {
      stats,
      config,
      statusLabel,
      modeLabel,
      factorDisplay,
      lastUpdateLabel: readonlyLastUpdate,
      cards: [
        {
          title: 'Estado del motor',
          value: statusLabel,
          helper: `Modo ${modeLabel}`,
          icon: <SmartToyIcon fontSize="small" />,
          footer: (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Actualizado {readonlyLastUpdate}
            </Typography>
          )
        },
        {
          title: 'Llamadas activas',
          value: stats.callsInProgress ?? 0,
          helper: `${stats.callsConnected ?? 0} conectadas / ${stats.callsFailed ?? 0} fallidas`,
          icon: <PhoneInTalkIcon fontSize="small" />
        },
        {
          title: 'Factor',
          value: factorDisplay,
          helper: `CPS objetivo ${config.callsPerSecond ?? config.cps ?? 'N/D'} | Simultáneas ${config.maxConcurrentCalls ?? config.maxConcurrent ?? 'N/D'}`,
          icon: <TimelineIcon fontSize="small" />
        },
        {
          title: 'Llamadas en espera',
          value: stats.connectionsWaiting ?? 0,
          helper: `Abandono ${Number(stats.abandonRate ?? 0).toFixed(2)}%`,
          icon: <DataUsageIcon fontSize="small" />
        }
      ]
    };
  }, [detail]);

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs>
              <Typography variant="h6" fontSize="1rem">
                Filtros
              </Typography>
            </Grid>
            <Grid item>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  color="inherit"
                  startIcon={<FilterAltOffIcon />}
                  onClick={() => {
                    setCampaignFilter('');
                    setStatusFilter('');
                    setModeFilter('');
                  }}
                  size="small"
                >
                  Limpiar
                </Button>
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={loadEngines}
                  disabled={listLoading}
                  size="small"
                >
                  Actualizar
                </Button>
              </Stack>
            </Grid>
          </Grid>

          <Grid container spacing={1.5} sx={{ rowGap: 1.5 }}>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                size="small"
                label="Campaña"
                value={campaignFilter}
                onChange={(e) => setCampaignFilter(e.target.value)}
              >
                <MenuItem value="">Todas</MenuItem>
                {campaignOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                size="small"
                label="Estado"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="active">Activos</MenuItem>
                <MenuItem value="inactive">Inactivos</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                size="small"
                label="Tipo de marcación"
                value={modeFilter}
                onChange={(e) => setModeFilter(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {modeOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" fontSize="1rem">
                Todos los Skills
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Dialer vs PBX, totalizado
              </Typography>
            </Box>
            <EngineOscillogram
              dialerHistory={totalDialerHistory}
              asteriskHistory={totalAsteriskHistory}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Totales</Typography>
                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                  <Typography variant="body2">
                    Dialer: {Number(totalDialerHistory[totalDialerHistory.length - 1] ?? 0).toLocaleString()} llamadas
                  </Typography>
                  <Typography variant="body2">
                    PBX: {Number(totalAsteriskHistory[totalAsteriskHistory.length - 1] ?? 0).toLocaleString()} llamadas
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Por Application</Typography>
                {renderBreakdownList(asteriskBreakdown.perApplication, 'Sin datos de aplicación')}
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Por State</Typography>
                {renderBreakdownList(asteriskBreakdown.perState, 'Sin datos de estado')}
              </Grid>
            </Grid>
          </Stack>
        </CardContent>
      </Card>

      {listLoading ? (
        <Box display="flex" alignItems="center" gap={2}>
          <CircularProgress size={24} />
          <Typography>Cargando motores...</Typography>
        </Box>
      ) : listError ? (
        <Alert severity="error">{listError}</Alert>
      ) : filteredSkills.length === 0 ? (
        <Alert severity="info">No hay skills que coincidan con los filtros seleccionados.</Alert>
      ) : (
        <Grid container spacing={2}>
          {filteredSkills.map(skill => {
            const engine = cardEngine(skill);
            const running = isRunning(skill);
            const paused = !!engine?.isPaused;
            const isSelected = selectedSkill?.skilldef === skill.skilldef;

            const modeLabel = getModeLabel(skill);
            const overdialingLabel = formatOverdialingLabel(
              skill.overdialing_mode || engine?.overdialing_mode || ''
            );
            const displayModeLabel = overdialingLabel || (modeLabel ? `Modo: ${modeLabel}` : '');
            return (
              <Grid item xs={12} sm={6} md={4} key={skill.skilldef || skill.id}>
                <Paper
                  elevation={isSelected ? 6 : 1}
                  onClick={() => handleSelectSkill(skill)}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    border: theme => isSelected
                      ? `2px solid ${theme.palette.primary.main}`
                      : '1px solid transparent',
                    transition: 'transform 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6">{skill.skillname || skill.skilldef}</Typography>
              <Typography variant="caption" color="text.secondary">
                {getCampaignLabel(skill)}
              </Typography>
            </Box>
          </Stack>
          <Divider sx={{ my: 1 }} />
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Stack direction="row" spacing={0.5} alignItems="center">
              <MemoryIcon fontSize="small" />
              <Typography variant="caption">
                {modeLabel || 'Modo sin definir'}
              </Typography>
            </Stack>
            {displayModeLabel && (
              <Stack direction="row" spacing={0.5} alignItems="flex-start">
                <SettingsInputComponentIcon fontSize="small" />
                <Typography variant="caption" sx={{ flex: 1 }}>
                  {displayModeLabel}
                </Typography>
              </Stack>
            )}
            <Stack direction="row" spacing={0.5} alignItems="center">
              <PowerSettingsNewIcon fontSize="small" color={running ? 'success' : 'disabled'} />
              <Typography variant="caption">
                {running ? 'Activo' : 'Inactivo'}
              </Typography>
            </Stack>
            {paused && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <PauseCircleOutlineIcon fontSize="small" color="warning" />
                <Typography variant="caption">Pausado</Typography>
              </Stack>
            )}
          </Stack>
          <Box mt={2}>
            <Typography variant="caption" color="text.secondary">
              Llamadas activas
            </Typography>
            <Typography variant="h5">
              {(engine?.statistics?.callsInProgress ?? 0).toLocaleString()}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              PBX: {Number(asteriskPerSkill[skill.skilldef] ?? 0).toLocaleString()} llamadas
            </Typography>
          </Box>
          <Box sx={{ mt: 1 }}>
            <EngineOscillogram
              dialerHistory={engineHistory[skill.skilldef] || []}
              asteriskHistory={asteriskHistory[skill.skilldef] || []}
            />
          </Box>
        </Paper>
      </Grid>
            );
          })}
        </Grid>
      )}

      <Box mt={4}>
        <Typography variant="h5" gutterBottom>Detalle del skill</Typography>
        {selectedSkill ? (
          detailLoading ? (
            <Box display="flex" alignItems="center" gap={2}>
              <CircularProgress size={20} />
              <Typography>Cargando detalle...</Typography>
            </Box>
          ) : detailError ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => loadDetail(selectedSkill.skilldef)}>
                  Reintentar
                </Button>
              }
            >
              {detailError}
            </Alert>
          ) : detail ? (
            <Grid container spacing={2}>
               <Grid item xs={12} md={6}>
                 <Paper sx={{ p: 2, height: '100%' }}>
                   <Typography variant="subtitle2" color="text.secondary">Resumen</Typography>
                   <Stack direction="row" spacing={2} flexWrap="wrap" mt={1} mb={2}>
                     <Stack direction="row" spacing={0.5} alignItems="center">
                       <MemoryIcon fontSize="small" />
                       <Typography variant="caption">
                         {detailMetrics?.modeLabel || 'Modo sin definir'}
                       </Typography>
                     </Stack>
                     {detailOverdialingLabel ? (
                       <Stack direction="row" spacing={0.5} alignItems="flex-start">
                         <SettingsInputComponentIcon fontSize="small" />
                         <Typography variant="caption" sx={{ flex: 1 }}>
                           {detailOverdialingLabel}
                         </Typography>
                       </Stack>
                     ) : detailMetrics?.modeLabel ? (
                       <Stack direction="row" spacing={0.5} alignItems="flex-start">
                         <SettingsInputComponentIcon fontSize="small" />
                         <Typography variant="caption" sx={{ flex: 1 }}>
                           {`Modo: ${detailMetrics.modeLabel}`}
                         </Typography>
                       </Stack>
                     ) : null}
                     <Stack direction="row" spacing={0.5} alignItems="center">
                       <PowerSettingsNewIcon fontSize="small" color={detail?.isRunning ? 'success' : 'disabled'} />
                       <Typography variant="caption">{detail?.isRunning ? 'Activo' : 'Inactivo'}</Typography>
                     </Stack>
                     {detail?.isPaused && (
                       <Stack direction="row" spacing={0.5} alignItems="center">
                         <PauseCircleOutlineIcon fontSize="small" color="warning" />
                         <Typography variant="caption">Pausado</Typography>
                       </Stack>
                     )}
                   </Stack>
                   <Grid container spacing={2}>
                     {detailMetrics?.cards?.map((card) => (
                       <Grid item xs={12} sm={6} key={card.title}>
                         <StatCard {...card} />
                       </Grid>
                     ))}
                   </Grid>
                   <Box
                     sx={{
                       mt: 2,
                       borderRadius: 2,
                       border: '1px solid',
                       borderColor: 'divider',
                       p: 2
                     }}
                   >
                     <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                       Rendimiento (ECG)
                     </Typography>
                     <Sparkline values={detailHistory} color="#00e676" />
                   </Box>
                 </Paper>
               </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, height: '100%' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Llamadas esperando agente
                  </Typography>
                  {detail.statistics?.waitingCalls?.length ? (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Action ID</TableCell>
                          <TableCell>Teléfono</TableCell>
                          <TableCell>Espera (s)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detail.statistics.waitingCalls.map(call => (
                          <TableRow key={call.actionId}>
                            <TableCell>{call.actionId}</TableCell>
                            <TableCell>{call.phone}</TableCell>
                            <TableCell>{call.waitTime}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No hay llamadas en cola esperando un agente.
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          ) : (
            <Alert severity="info">Selecciona un skill para ver su detalle.</Alert>
          )
        ) : (
          <Alert severity="info">Selecciona un skill en la cuadricula para inspeccionar su motor.</Alert>
        )}
      </Box>
    </Box>
  );
};

export default EngineStatusPage;
